/**
 * @fileoverview Counts the signed-in student's code runs on the assignment
 * they have open, for the TeachNow fields on their presence doc. Module
 * state: one IDE per tab.
 */

/** Runs on the open assignment, as written to presence. */
export interface IdeRunActivity {
  /** Runs (and test runs) since the assignment was opened. */
  runsOnAssn: number;
  /** Of those, runs that ended in an error or a failing test. */
  failedRunsOnAssn: number;
  /** Whether the latest run failed; `null` before the first run. */
  lastRunFailed: boolean | null;
  /** Epoch ms (server clock when known) of the latest run; `null` before. */
  lastRunMs: number | null;
}

let assnId: string | null = null;
let activity: IdeRunActivity = emptyActivity();

function emptyActivity(): IdeRunActivity {
  return {runsOnAssn: 0, failedRunsOnAssn: 0, lastRunFailed: null, lastRunMs: null};
}

/**
 * Starts counting for a newly opened assignment, or stops counting. Re-opening
 * the same one keeps its counts.
 *
 * @param nextAssnId - The assignment now open, or `null` once the student
 *   leaves the IDE, which clears the counts and ignores runs until the next
 *   assignment (for example, embedded IDEs on lesson pages).
 * @return Nothing.
 * @throws Never.
 */
export function startIdeRunActivity(nextAssnId: string | null): void {
  if (nextAssnId === assnId) {
    return;
  }
  assnId = nextAssnId;
  activity = emptyActivity();
}

/**
 * Records one finished run (or test run) on the open assignment. Does nothing
 * while no assignment is open.
 *
 * @param args.isFailed - Whether it ended in an error or a failing test.
 * @param args.nowMs - When it finished, epoch ms.
 * @return Nothing.
 * @throws Never.
 */
export function recordIdeRun({
  isFailed,
  nowMs,
}: {
  isFailed: boolean;
  nowMs: number;
}): void {
  if (assnId === null) {
    return;
  }
  activity = {
    runsOnAssn: activity.runsOnAssn + 1,
    failedRunsOnAssn: activity.failedRunsOnAssn + (isFailed ? 1 : 0),
    lastRunFailed: isFailed,
    lastRunMs: nowMs,
  };
}

/**
 * Marks the latest run as failed without counting another run, for checks
 * that belong to the same click (Karel runs the program, then its tests).
 * Does nothing while no assignment is open, before the first run, or when
 * the latest run already failed.
 *
 * @param args.nowMs - When the check finished, epoch ms.
 * @return Nothing.
 * @throws Never.
 */
export function markLastIdeRunFailed({nowMs}: {nowMs: number}): void {
  if (assnId === null || activity.runsOnAssn === 0 || activity.lastRunFailed) {
    return;
  }
  activity = {
    ...activity,
    failedRunsOnAssn: activity.failedRunsOnAssn + 1,
    lastRunFailed: true,
    lastRunMs: nowMs,
  };
}

/**
 * The counts so far for the open assignment.
 *
 * @return A copy of the counts.
 * @throws Never.
 */
export function getIdeRunActivity(): IdeRunActivity {
  return {...activity};
}

/**
 * Runs a TeachNow run-count update from the IDE's run path, so that no
 * failure in it can reach the student's Run or test click.
 *
 * @param update - Reads the run's result and records it.
 * @return Nothing.
 * @throws Never.
 */
export function noteIdeRun(update: () => void): void {
  try {
    update();
  } catch (error) {
    console.debug('[teachnow] run count update failed', error);
  }
}
